import {useLocation} from 'react-router-dom';

/**
 * Query params identify the person (token, firstName, email, code, ...), and the Facebook pixel,
 * Segment and Sentry all send window.location with every event. The inline script near the top of
 * `index.html` therefore moves the query string into `history.state` and shows only the bare path
 * (keeping utm_* / fbclid / gclid), for the first load and for every pushState/replaceState.
 *
 * Read params through this module, never from `window.location.search` or `useLocation().search`
 * directly: those are now empty (or hold only the marketing params) on a scrubbed page.
 * `src/utils/Analytics.ts` is the one deliberate exception — it should only ever see the clean URL.
 */

/** Same key as the inline script in index.html. Change both or neither. */
export const QUERY_STATE_KEY = 'riseQuery';

/** Params that stay in the visible URL. Same list as the inline script in index.html. */
const PUBLIC_PARAM = /^(utm_[a-z0-9_]+|fbclid|gclid)$/i;

/**
 * `url` with every query param except the public ones removed, for code that captures a URL the
 * scrub hasn't seen yet (Sentry's navigation breadcrumbs record the pushState argument).
 */
export const stripPersonalParams = (url: string): string => {
  const queryStart = url.indexOf('?');
  const firstHash = url.indexOf('#');
  if (queryStart === -1 || (firstHash !== -1 && firstHash < queryStart)) {
    return url;
  }
  const hashStart = url.indexOf('#', queryStart);
  const query = url.slice(queryStart + 1, hashStart === -1 ? undefined : hashStart);
  const hash = hashStart === -1 ? '' : url.slice(hashStart);
  const kept = new URLSearchParams();
  new URLSearchParams(query).forEach((value, key) => {
    if (PUBLIC_PARAM.test(key)) {
      kept.append(key, value);
    }
  });
  const keptQuery = kept.toString();
  return `${url.slice(0, queryStart)}${keptQuery ? `?${keptQuery}` : ''}${hash}`;
};

const stashedSearch = (): string | null => {
  const state = window.history.state;
  if (state && typeof state === 'object' && typeof state[QUERY_STATE_KEY] === 'string') {
    return state[QUERY_STATE_KEY];
  }
  return null;
};

/**
 * The page's original query string (with the leading `?`), falling back to the live URL when
 * nothing was scrubbed (tests, local tooling, a page with only marketing params).
 */
export const getSearch = (): string => stashedSearch() ?? window.location.search;

export const getSearchParams = (): URLSearchParams => new URLSearchParams(getSearch());

/**
 * The page's full original URL, query included, for links that must bring the person back here
 * (Stripe's cancel_url). `window.location.href` no longer carries the token on a scrubbed page.
 */
export const currentUrl = (): string =>
  `${window.location.origin}${window.location.pathname}${getSearch()}${window.location.hash}`;

/** Hook form of `getSearch`; subscribes to navigation so it re-reads on every route change. */
export const useSearch = (): string => {
  const location = useLocation();
  return stashedSearch() ?? location.search;
};

export const useSearchParams = (): URLSearchParams => new URLSearchParams(useSearch());
